// Cliente HTTP para el backend (auth, estado persistido, subidas).
import { BACKEND_BASE } from "./backendUrl";
export const BASE = BACKEND_BASE;

const formatDetail = (detail) => {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  return JSON.stringify(detail);
};

const request = async (path, options = {}) => {
  const defaultHeaders =
    options.body && !(options.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {};
  const { headers: customHeaders, ...restOptions } = options;
  const mergedHeaders = {
    ...defaultHeaders,
    ...(customHeaders || {}),
  };
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...restOptions,
    headers: mergedHeaders,
  });
  if (!res.ok) {
    let detail = `http_${res.status}`;
    try {
      const body = await res.json();
      detail = formatDetail(body.detail) || detail;
    } catch { /* no-op: respuesta sin cuerpo JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

const isIsolatedProposalMode = () => {
  if (process.env.NODE_ENV === "production") return false;
  if (typeof window === "undefined") return false;
  try {
    if (window.location.search && window.location.search.includes("test_isolated_proposal=1")) {
      window.sessionStorage.setItem("veyra_isolated_proposal", "1");
      return true;
    }
    return window.sessionStorage.getItem("veyra_isolated_proposal") === "1";
  } catch {
    return false;
  }
};

const getIsolatedState = async () => {
  try {
    const cached = window.sessionStorage.getItem("veyra_isolated_state");
    if (cached) {
      return JSON.parse(cached);
    }
  } catch { /* fallback */ }

  let baseState = null;
  try {
    baseState = await request("/api/state");
  } catch { /* no-op */ }

  if (!baseState) {
    baseState = {
      projects: [{ id: "proj_isolated_test", name: "Proyecto Piloto Aislado" }],
      activeProjectId: "proj_isolated_test",
      scenes: [],
      threads: [],
    };
  }

  const activePid = baseState.activeProjectId || (baseState.projects?.[0]?.id) || "proj_isolated_test";
  const threads = Array.isArray(baseState.threads) ? [...baseState.threads] : [];
  let thIdx = threads.findIndex((t) => t.projectId === activePid);

  const mockProposal = {
    id: "prop_mock_01",
    title: "CampaÃ±a Urbana (2 tomas)",
    applied: false,
    appliedIndices: [],
    scenes: [
      {
        title: "Toma 1: Tomas aÃ©reas del puente",
        prompt: "@Escena 1 [5s] Drone volando sobre el puente al amanecer con neblina dorada.",
        duration: 5,
        referenceIds: ["ref_puente", "ref_luz_dorada"],
      },
      {
        title: "Toma 2: Coche cruzando",
        prompt: "@Escena 2 [4s] VehÃ­culo deportivo rojo acelera por la calzada hÃºmeda.",
        duration: 4,
        referenceIds: ["ref_auto_rojo"],
      },
    ],
  };

  const mockMsg = {
    id: "msg_test_proposal",
    role: "assistant",
    content: "He preparado una propuesta cinematogrÃ¡fica de 2 tomas para tu revisiÃ³n:",
    proposals: [mockProposal],
  };

  if (thIdx === -1) {
    threads.push({ projectId: activePid, messages: [mockMsg] });
  } else {
    const existingMessages = Array.isArray(threads[thIdx].messages) ? [...threads[thIdx].messages] : [];
    if (!existingMessages.some((m) => m.id === "msg_test_proposal")) {
      threads[thIdx] = {
        ...threads[thIdx],
        messages: [...existingMessages, mockMsg],
      };
    }
  }

  const isolatedState = {
    ...baseState,
    threads,
  };

  try {
    window.sessionStorage.setItem("veyra_isolated_state", JSON.stringify(isolatedState));
  } catch { /* no-op */ }

  return isolatedState;
};

const saveIsolatedState = async (state) => {
  try {
    window.sessionStorage.setItem("veyra_isolated_state", JSON.stringify(state));
  } catch { /* no-op */ }
  return { ok: true, isolated: true };
};

export const api = {
  setupStatus: () => request("/api/auth/setup-status"),
  setup: (payload) => request("/api/auth/setup", { method: "POST", body: JSON.stringify(payload) }),
  reclaimOwner: (payload) => request("/api/auth/owner/reclaim", { method: "POST", body: JSON.stringify(payload) }),
  login: (payload) => request("/api/auth/login", { method: "POST", body: JSON.stringify(payload) }),
  logout: () => request("/api/auth/logout", { method: "POST" }),
  me: () => request("/api/auth/me"),
  // Invitaciones y gestión de usuarios (Bloque 1)
  createInvitation: (payload, options = {}) =>
    request("/api/auth/invitations", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        ...(options.idempotencyKey ? { "Idempotency-Key": options.idempotencyKey } : {}),
        ...(options.headers || {}),
      },
    }),
  resendInvitation: (invitationId, options = {}) =>
    request(`/api/auth/invitations/${invitationId}/resend`, {
      method: "POST",
      headers: options.idempotencyKey ? { "Idempotency-Key": options.idempotencyKey } : {},
    }),
  getInvitationEmailStatus: () => request("/api/auth/invitations/email-status"),
  listInvitations: () => request("/api/auth/invitations"),
  revokeInvitation: (invitationId) => request(`/api/auth/invitations/${invitationId}/revoke`, { method: "POST" }),
  validateInvitation: (token) => request(`/api/auth/invitations/validate?token=${encodeURIComponent(token)}`),
  acceptInvitation: (payload) => request("/api/auth/invitations/accept", { method: "POST", body: JSON.stringify(payload) }),
  listUsers: () => request("/api/auth/users"),
  updateUserStatus: (userId, payload) => request(`/api/auth/users/${userId}/status`, { method: "POST", body: JSON.stringify(payload) }),
  // Conexiones de generaciÃ³n por usuario (Bloque 2A)
  listUserConnections: () => request("/api/user-connections"),
  saveUserConnection: (payload) => request("/api/user-connections", { method: "POST", body: JSON.stringify(payload) }),
  deleteUserConnection: (providerId) => request(`/api/user-connections/${providerId}`, { method: "DELETE" }),
  getState: () => (isIsolatedProposalMode() ? getIsolatedState() : request("/api/state")),
  saveState: (state) => (isIsolatedProposalMode() ? saveIsolatedState(state) : request("/api/state", { method: "PUT", body: JSON.stringify(state) })),
  // CatÃ¡logo de proveedores (fuente Ãºnica de verdad â€” siempre viene del backend)
  getCatalog: () => request("/api/catalog/providers"),
  // GeneraciÃ³n
  submitGeneration: (payload) => request("/api/generate", { method: "POST", body: JSON.stringify(payload) }),
  getJob: (jobId) => request(`/api/generate/${jobId}`),
  simulateJob: (jobId) => request(`/api/generate/${jobId}/simulate`, { method: "POST" }),
  recoverJob: (jobId) => request(`/api/generate/${jobId}/recover`, { method: "POST" }),
  listJobs: () => request("/api/generate"),

  submitUpscale: (payload) => request("/api/upscale", { method: "POST", body: JSON.stringify(payload) }),
  getUpscaleJob: (jobId) => request(`/api/upscale/${jobId}`),
  listUpscaleJobs: () => request("/api/upscale"),
  deleteAssets: (assetIds) => request("/api/uploads/batch", { method: "DELETE", body: JSON.stringify({ asset_ids: assetIds }) }),
  // Papelera de escenas en el servidor (persistente y aislada por usuario y proyecto)
  getTrashScenes: (projectId) => request(`/api/trash/scenes?project_id=${encodeURIComponent(projectId)}`),
  addSceneToTrash: (data) => request("/api/trash/scenes", { method: "POST", body: JSON.stringify(data) }),
  restoreSceneFromTrash: (sceneId, projectId = null) =>
    request(`/api/trash/scenes/${encodeURIComponent(sceneId)}/restore${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ""}`, {
      method: "POST",
    }),
  syncLocalTrash: (data) => request("/api/trash/scenes/sync-local", { method: "POST", body: JSON.stringify(data) }),
  purgeTrashPreview: (data) =>
    request("/api/trash/scenes/purge-preview", { method: "POST", body: JSON.stringify(data) }),
  purgeTrashScenes: (data) =>
    request("/api/trash/scenes/purge", { method: "POST", body: JSON.stringify(data) }),
  // Metadatos de activo (sin contenido binario)
  changePassword: (payload) => request("/api/auth/change-password", { method: "POST", body: JSON.stringify(payload) }),
  getBudgetStatus: () => request("/api/budget/status"),
  updateBudgetLimit: (monthlyLimitUsd) =>
    request("/api/budget/limit", {
      method: "PUT",
      body: JSON.stringify({ monthly_limit_usd: monthlyLimitUsd }),
    }),
  getBudgetHistory: (limit = 20) => request(`/api/budget/history?limit=${limit}`),
  // InspecciÃ³n de continuidad cinematogrÃ¡fica (Fase 2B.3B)
  inspectContinuity: (projectId) =>
    request("/api/assistant/continuity/inspect", {
      method: "POST",
      body: JSON.stringify({ project_id: projectId }),
    }),
  getKlingAuthStatus: () => request("/api/generate/kling/auth-status"),
  getAdminStatus: () => request("/api/admin/status"),
  getLLMTemplates: () => request("/api/admin/llm/templates"),
  getLLMProviders: () => request("/api/admin/llm/providers"),
  getLLMProvider: (id) => request(`/api/admin/llm/providers/${id}`),
  createLLMProvider: (payload) => request("/api/admin/llm/providers", { method: "POST", body: JSON.stringify(payload) }),
  updateLLMProvider: (id, payload) => request(`/api/admin/llm/providers/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteLLMProvider: (id) => request(`/api/admin/llm/providers/${id}`, { method: "DELETE" }),
  testLLMProvider: (id, payload) => request(`/api/admin/llm/providers/${id}/test`, { method: "POST", body: JSON.stringify(payload) }),
  getAssistantModels: () => request("/api/assistant/models"),
  analyzeCharacterImage: (payload) =>
    request("/api/assistant/characters/analyze-image", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  uploadAsset: (blob, filename) => uploadAsset(blob, filename),
  uploadAssetWithProgress: (blob, opts) => uploadAssetWithProgress(blob, opts),
};

export const uploadAsset = async (blob, filename) => {
  const form = new FormData();
  form.append("file", blob, filename || "upload.png");
  const res = await fetch(`${BASE}/api/uploads`, { method: "POST", credentials: "include", body: form });
  if (!res.ok) {
    let detail = `http_${res.status}`;
    try { const body = await res.json(); detail = formatDetail(body.detail) || detail; } catch { /* no-op */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return { ...data, url: `${BASE}${data.url}` };
};

export const uploadAssetWithProgress = (blob, { filename, onProgress } = {}) => {
  return new Promise((resolve, reject) => {
    if (typeof XMLHttpRequest === "undefined") {
      uploadAsset(blob, filename).then(resolve).catch(reject);
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/api/uploads`);
    xhr.withCredentials = true;

    if (xhr.upload && typeof onProgress === "function") {
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && evt.total > 0) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          onProgress(pct);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ...data, url: `${BASE}${data.url}` });
        } catch {
          reject(new Error("invalid_json_response"));
        }
      } else {
        let detail = `http_${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText);
          detail = formatDetail(body.detail) || detail;
        } catch { /* no-op */ }
        const err = new Error(detail);
        err.status = xhr.status;
        reject(err);
      }
    };

    xhr.onerror = () => {
      reject(new Error("network_error"));
    };

    const form = new FormData();
    form.append("file", blob, filename || blob.name || "upload.png");
    xhr.send(form);
  });
};
